import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, Globe, User, Save, Plus, Edit, Trash2, X, GripVertical } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { View } from '@/models/view';
import { FilterField } from '@/components/shared/filters/InlineFilters';
import { Column } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { Plus as PlusIcon, X as XIcon } from 'lucide-react';

interface ViewSelectorProps {
    views: View[];
    selectedViewId: string | null;
    onViewSelect: (viewId: string | null) => void;
    onSaveAsView: (name: string, description?: string, isGlobal?: boolean) => Promise<View>;
    onUpdateView?: (viewId: string, updates: Partial<View>) => Promise<void>;
    onDeleteView?: (viewId: string) => Promise<void>;
    onReorderViews?: (viewIds: string[]) => Promise<void>;
    isSavingView: boolean;
    isAdmin: boolean;
    isLoading?: boolean;
    filterFields?: FilterField[];
    columns?: Column<any>[];
}

export const ViewSelector: React.FC<ViewSelectorProps> = ({
    views,
    selectedViewId,
    onViewSelect,
    onSaveAsView,
    onUpdateView,
    onDeleteView,
    onReorderViews,
    isSavingView,
    isAdmin,
    isLoading = false,
    filterFields = [],
    columns = [],
}) => {
    const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
    const [saveViewName, setSaveViewName] = useState('');
    const [saveViewDescription, setSaveViewDescription] = useState('');
    const [saveAsGlobal, setSaveAsGlobal] = useState(false);
    const [editingView, setEditingView] = useState<View | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deletingView, setDeletingView] = useState<View | null>(null);
    /** Popover portal target so multi-select stacks above the dialog overlay (body portals sit behind). */
    const [editViewDialogEl, setEditViewDialogEl] = useState<HTMLElement | null>(null);

    // Separate views into global and user-specific
    const globalViews = views.filter(v => v.is_global);
    const userViews = views.filter(v => !v.is_global);

    const handleSaveView = async () => {
        if (!saveViewName.trim()) return;
        try {
            await onSaveAsView(saveViewName.trim(), saveViewDescription.trim(), saveAsGlobal);
            setIsSaveDialogOpen(false);
            setSaveViewName('');
            setSaveViewDescription('');
            setSaveAsGlobal(false);
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    const handleEditView = (view: View) => {
        setEditingView({ ...view });
        setIsEditDialogOpen(true);
    };

    const handleUpdateView = async () => {
        if (!editingView || !onUpdateView) return;
        try {
            await onUpdateView(editingView.id, {
                name: editingView.name,
                description: editingView.description,
                filters: editingView.filters,
                sort: editingView.sort,
                visible_columns: editingView.visible_columns,
            });
            setIsEditDialogOpen(false);
            setEditingView(null);
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    const handleDeleteView = async () => {
        if (!deletingView || !onDeleteView) return;
        try {
            await onDeleteView(deletingView.id);
            setIsDeleteDialogOpen(false);
            setDeletingView(null);
            if (selectedViewId === deletingView.id) {
                onViewSelect(null);
            }
        } catch (error) {
            // Error handling is done in the hook
        }
    };

    // Filter management in edit dialog
    const addFilter = (fieldKey: string) => {
        if (!editingView) return;
        const field = filterFields.find(f => f.key === fieldKey);
        if (!field) return;

        const newFilters = { ...(editingView.filters || {}) };
        // Set default value based on field type
        if (field.type === 'boolean') {
            newFilters[fieldKey] = true;
        } else if (field.type === 'number') {
            newFilters[fieldKey] = '';
        } else {
            newFilters[fieldKey] = '';
        }

        setEditingView({
            ...editingView,
            filters: newFilters,
        });
    };

    const removeFilter = (fieldKey: string) => {
        if (!editingView) return;
        const newFilters = { ...(editingView.filters || {}) };
        delete newFilters[fieldKey];
        setEditingView({
            ...editingView,
            filters: newFilters,
        });
    };

    const updateFilter = (fieldKey: string, value: any) => {
        if (!editingView) return;
        setEditingView({
            ...editingView,
            filters: {
                ...(editingView.filters || {}),
                [fieldKey]: value,
            },
        });
    };

    const availableFilterFields = useMemo(() => {
        if (!editingView) return filterFields;
        const usedKeys = Object.keys(editingView.filters || {});
        return filterFields.filter(field => !usedKeys.includes(field.key));
    }, [filterFields, editingView]);

    const sortableColumns = useMemo(() => {
        return columns.filter(col => col.sortable !== false);
    }, [columns]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent, listType: 'global' | 'personal') => {
            const { active, over } = event;
            if (!over || active.id === over.id || !onReorderViews) return;

            const list = listType === 'global' ? globalViews : userViews;
            const otherList = listType === 'global' ? userViews : globalViews;
            const oldIndex = list.findIndex(v => v.id === active.id);
            const newIndex = list.findIndex(v => v.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return;

            const reordered = arrayMove(list, oldIndex, newIndex);
            const viewIds =
                listType === 'global'
                    ? [...reordered.map(v => v.id), ...otherList.map(v => v.id)]
                    : [...otherList.map(v => v.id), ...reordered.map(v => v.id)];
            onReorderViews(viewIds);
        },
        [globalViews, userViews, onReorderViews]
    );

    const canReorderGlobal = Boolean(onReorderViews && isAdmin);
    const canReorderPersonal = Boolean(onReorderViews);

    const renderViewChips = (
        listViews: View[],
        canReorder: boolean,
        icon: React.ReactNode,
        showDefaultBadge: boolean
    ) => (
        <div className="flex items-center gap-1 flex-wrap">
            {listViews
                .filter(view => view.id != null)
                .map(view => (
                    <SortableViewChip
                        key={view.id}
                        view={view}
                        selectedViewId={selectedViewId}
                        onViewSelect={onViewSelect}
                        onEditView={handleEditView}
                        isAdmin={isAdmin}
                        isLoading={isLoading}
                        canReorder={canReorder}
                        icon={icon}
                        showDefaultBadge={showDefaultBadge}
                    />
                ))}
        </div>
    );

    const viewListContent = (
        <>
            <span className="text-sm font-medium text-foreground">Views:</span>
            {globalViews.length > 0 &&
                renderViewChips(globalViews, false, <Globe className="h-3 w-3" />, true)}
            {userViews.length > 0 &&
                renderViewChips(userViews, false, <User className="h-3 w-3" />, false)}
            <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSaveDialogOpen(true)}
                disabled={isSavingView || isLoading}
                className="text-xs"
            >
                <Save className="h-3 w-3 mr-1" />
                Save as View
            </Button>
        </>
    );

    return (
        <>
            <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                {onReorderViews ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e: DragEndEvent) => {
                            const activeId = e.active.id as string;
                            if (globalViews.some(v => v.id === activeId)) {
                                handleDragEnd(e, 'global');
                            } else {
                                handleDragEnd(e, 'personal');
                            }
                        }}
                    >
                        <span className="text-sm font-medium text-foreground">Views:</span>
                        {globalViews.length > 0 && (
                            <SortableContext
                                items={globalViews.map(v => v.id)}
                                strategy={horizontalListSortingStrategy}
                            >
                                {renderViewChips(
                                    globalViews,
                                    canReorderGlobal,
                                    <Globe className="h-3 w-3" />,
                                    true
                                )}
                            </SortableContext>
                        )}
                        {userViews.length > 0 && (
                            <SortableContext
                                items={userViews.map(v => v.id)}
                                strategy={horizontalListSortingStrategy}
                            >
                                {renderViewChips(
                                    userViews,
                                    canReorderPersonal,
                                    <User className="h-3 w-3" />,
                                    false
                                )}
                            </SortableContext>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsSaveDialogOpen(true)}
                            disabled={isSavingView || isLoading}
                            className="text-xs"
                        >
                            <Save className="h-3 w-3 mr-1" />
                            Save as View
                        </Button>
                    </DndContext>
                ) : (
                    viewListContent
                )}
            </div>

            {/* Save View Dialog */}
            <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Save as View</DialogTitle>
                        <DialogDescription>
                            Save current filters, sorting, and visible columns as a view
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="view-name">View Name *</Label>
                            <Input
                                id="view-name"
                                value={saveViewName}
                                onChange={e => setSaveViewName(e.target.value)}
                                placeholder="Enter view name"
                            />
                        </div>
                        <div>
                            <Label htmlFor="view-description">Description</Label>
                            <Textarea
                                id="view-description"
                                value={saveViewDescription}
                                onChange={e => setSaveViewDescription(e.target.value)}
                                placeholder="Enter view description (optional)"
                            />
                        </div>
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="save-as-global"
                                    checked={saveAsGlobal}
                                    onChange={e => setSaveAsGlobal(e.target.checked)}
                                />
                                <Label htmlFor="save-as-global">
                                    Save as global view (visible to all users)
                                </Label>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsSaveDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveView}
                            disabled={!saveViewName.trim() || isSavingView}
                        >
                            {isSavingView ? 'Saving...' : 'Save View'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit View Dialog */}
            {editingView && (
                <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                    <DialogContent
                        ref={setEditViewDialogEl}
                        className="max-w-3xl max-h-[90vh] flex flex-col gap-0 overflow-visible p-0"
                    >
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6">
                            <DialogHeader>
                                <DialogTitle>Edit View</DialogTitle>
                                <DialogDescription>
                                    {editingView.is_global
                                        ? 'Edit global view (visible to all users)'
                                        : 'Edit your personal view'}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="mt-6 space-y-6">
                                {/* Basic Info */}
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="edit-view-name">View Name *</Label>
                                        <Input
                                            id="edit-view-name"
                                            value={editingView.name}
                                            onChange={e =>
                                                setEditingView({
                                                    ...editingView,
                                                    name: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="edit-view-description">Description</Label>
                                        <Textarea
                                            id="edit-view-description"
                                            value={editingView.description || ''}
                                            onChange={e =>
                                                setEditingView({
                                                    ...editingView,
                                                    description: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                {/* Filters Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-semibold">Filters</Label>
                                        {availableFilterFields.length > 0 && (
                                            <Select value="" onValueChange={addFilter}>
                                                <SelectTrigger className="w-[200px]">
                                                    <SelectValue placeholder="Add filter..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableFilterFields.map(field => (
                                                        <SelectItem
                                                            key={field.key}
                                                            value={field.key}
                                                        >
                                                            {field.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                    <div className="space-y-2 border rounded-lg p-3 min-h-[100px]">
                                        {Object.keys(editingView.filters || {}).length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                No filters added. Click "Add filter..." to add one.
                                            </p>
                                        ) : (
                                            Object.entries(editingView.filters || {}).map(
                                                ([key, value]) => {
                                                    const field = filterFields.find(
                                                        f => f.key === key
                                                    );
                                                    if (!field) return null;

                                                    return (
                                                        <div
                                                            key={key}
                                                            className="flex items-center gap-2 p-2 bg-muted/50 rounded border"
                                                        >
                                                            <Label className="min-w-[120px] text-sm">
                                                                {field.label}:
                                                            </Label>
                                                            <div className="flex-1">
                                                                {field.type === 'select' ? (
                                                                    (() => {
                                                                        const selectedIds: string[] =
                                                                            value
                                                                                ? String(value)
                                                                                      .split(',')
                                                                                      .map(
                                                                                          (
                                                                                              s: string
                                                                                          ) =>
                                                                                              s.trim()
                                                                                      )
                                                                                      .filter(
                                                                                          Boolean
                                                                                      )
                                                                                : [];
                                                                        const validOptions = (
                                                                            field.options || []
                                                                        ).filter(
                                                                            (opt: {
                                                                                value: string;
                                                                                label: string;
                                                                            }) =>
                                                                                opt.value !== '' &&
                                                                                opt.value !==
                                                                                    null &&
                                                                                opt.value !==
                                                                                    undefined
                                                                        );
                                                                        const triggerLabel =
                                                                            selectedIds.length === 0
                                                                                ? 'Select...'
                                                                                : selectedIds.length ===
                                                                                    1
                                                                                  ? (validOptions.find(
                                                                                        (o: {
                                                                                            value: string;
                                                                                        }) =>
                                                                                            o.value ===
                                                                                            selectedIds[0]
                                                                                    )?.label ??
                                                                                    selectedIds[0])
                                                                                  : `${field.label} (${selectedIds.length})`;
                                                                        return (
                                                                            <Popover>
                                                                                <PopoverTrigger
                                                                                    asChild
                                                                                >
                                                                                    <Button
                                                                                        variant="outline"
                                                                                        role="combobox"
                                                                                        className={cn(
                                                                                            'h-8 min-w-[160px] justify-between font-normal'
                                                                                        )}
                                                                                    >
                                                                                        <span className="truncate">
                                                                                            {
                                                                                                triggerLabel
                                                                                            }
                                                                                        </span>
                                                                                        <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                                                                                    </Button>
                                                                                </PopoverTrigger>
                                                                                <PopoverContent
                                                                                    container={
                                                                                        editViewDialogEl
                                                                                    }
                                                                                    className="min-w-[200px] p-0"
                                                                                    align="start"
                                                                                    onOpenAutoFocus={e =>
                                                                                        e.preventDefault()
                                                                                    }
                                                                                >
                                                                                    <div className="max-h-60 overflow-y-auto p-1">
                                                                                        {validOptions.map(
                                                                                            (option: {
                                                                                                value: string;
                                                                                                label: string;
                                                                                            }) => {
                                                                                                const checked =
                                                                                                    selectedIds.includes(
                                                                                                        String(
                                                                                                            option.value
                                                                                                        )
                                                                                                    );
                                                                                                return (
                                                                                                    <label
                                                                                                        key={
                                                                                                            option.value
                                                                                                        }
                                                                                                        className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                                                                                                    >
                                                                                                        <Checkbox
                                                                                                            checked={
                                                                                                                checked
                                                                                                            }
                                                                                                            onCheckedChange={() => {
                                                                                                                const next =
                                                                                                                    checked
                                                                                                                        ? selectedIds.filter(
                                                                                                                              (
                                                                                                                                  id: string
                                                                                                                              ) =>
                                                                                                                                  id !==
                                                                                                                                  String(
                                                                                                                                      option.value
                                                                                                                                  )
                                                                                                                          )
                                                                                                                        : [
                                                                                                                              ...selectedIds,
                                                                                                                              String(
                                                                                                                                  option.value
                                                                                                                              ),
                                                                                                                          ];
                                                                                                                updateFilter(
                                                                                                                    key,
                                                                                                                    next.length
                                                                                                                        ? next.join(
                                                                                                                              ','
                                                                                                                          )
                                                                                                                        : ''
                                                                                                                );
                                                                                                            }}
                                                                                                        />
                                                                                                        <span className="truncate">
                                                                                                            {
                                                                                                                option.label
                                                                                                            }
                                                                                                        </span>
                                                                                                    </label>
                                                                                                );
                                                                                            }
                                                                                        )}
                                                                                    </div>
                                                                                </PopoverContent>
                                                                            </Popover>
                                                                        );
                                                                    })()
                                                                ) : field.type === 'boolean' ? (
                                                                    <Select
                                                                        value={String(value || '')}
                                                                        onValueChange={val =>
                                                                            updateFilter(
                                                                                key,
                                                                                val === 'true'
                                                                            )
                                                                        }
                                                                    >
                                                                        <SelectTrigger className="h-8">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="true">
                                                                                Yes
                                                                            </SelectItem>
                                                                            <SelectItem value="false">
                                                                                No
                                                                            </SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                ) : (
                                                                    <Input
                                                                        type={
                                                                            field.type === 'number'
                                                                                ? 'number'
                                                                                : field.type ===
                                                                                    'date'
                                                                                  ? 'date'
                                                                                  : 'text'
                                                                        }
                                                                        value={value || ''}
                                                                        onChange={e =>
                                                                            updateFilter(
                                                                                key,
                                                                                field.type ===
                                                                                    'number'
                                                                                    ? Number(
                                                                                          e.target
                                                                                              .value
                                                                                      ) || ''
                                                                                    : e.target.value
                                                                            )
                                                                        }
                                                                        className="h-8"
                                                                        placeholder={
                                                                            field.placeholder
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeFilter(key)}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <XIcon className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    );
                                                }
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Sort Section */}
                                <div className="space-y-4">
                                    <Label className="text-base font-semibold">Sort</Label>
                                    <div className="flex items-center gap-2">
                                        <Select
                                            value={editingView.sort?.key || ''}
                                            onValueChange={key =>
                                                setEditingView({
                                                    ...editingView,
                                                    sort: {
                                                        key,
                                                        direction:
                                                            editingView.sort?.direction || 'asc',
                                                    },
                                                })
                                            }
                                        >
                                            <SelectTrigger className="flex-1">
                                                <SelectValue placeholder="Select column..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {sortableColumns.map(col => (
                                                    <SelectItem
                                                        key={col.id}
                                                        value={col.key as string}
                                                    >
                                                        {col.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={editingView.sort?.direction || 'asc'}
                                            onValueChange={direction =>
                                                setEditingView({
                                                    ...editingView,
                                                    sort: {
                                                        key: editingView.sort?.key || '',
                                                        direction: direction as 'asc' | 'desc',
                                                    },
                                                })
                                            }
                                        >
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="asc">Ascending</SelectItem>
                                                <SelectItem value="desc">Descending</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {editingView.sort?.key && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    setEditingView({
                                                        ...editingView,
                                                        sort: undefined,
                                                    })
                                                }
                                                className="h-8"
                                            >
                                                <XIcon className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Visible Columns Section */}
                                <div className="space-y-4">
                                    <Label className="text-base font-semibold">
                                        Visible Columns
                                    </Label>
                                    <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto">
                                        <div className="space-y-2">
                                            {columns
                                                .filter(
                                                    col =>
                                                        col.id !== 'selection' &&
                                                        col.id !== 'actions'
                                                )
                                                .map(column => (
                                                    <div
                                                        key={column.id}
                                                        className="flex items-center space-x-2"
                                                    >
                                                        <Checkbox
                                                            id={`col-${column.id}`}
                                                            checked={(
                                                                editingView.visible_columns || []
                                                            ).includes(column.id)}
                                                            onCheckedChange={checked => {
                                                                const currentColumns =
                                                                    editingView.visible_columns ||
                                                                    [];
                                                                if (checked) {
                                                                    setEditingView({
                                                                        ...editingView,
                                                                        visible_columns: [
                                                                            ...currentColumns,
                                                                            column.id,
                                                                        ],
                                                                    });
                                                                } else {
                                                                    setEditingView({
                                                                        ...editingView,
                                                                        visible_columns:
                                                                            currentColumns.filter(
                                                                                id =>
                                                                                    id !== column.id
                                                                            ),
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                        <Label
                                                            htmlFor={`col-${column.id}`}
                                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                        >
                                                            {column.label}
                                                        </Label>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <DialogFooter className="flex shrink-0 items-center justify-between border-t bg-background px-6 py-4">
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    setDeletingView(editingView);
                                    setIsEditDialogOpen(false);
                                    setIsDeleteDialogOpen(true);
                                }}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsEditDialogOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateView}
                                    disabled={!editingView.name.trim()}
                                >
                                    Update View
                                </Button>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete View Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete View</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{deletingView?.name}"? This action
                            cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteView}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

interface SortableViewChipProps {
    view: View;
    selectedViewId: string | null;
    onViewSelect: (viewId: string | null) => void;
    onEditView: (view: View) => void;
    isAdmin: boolean;
    isLoading: boolean;
    canReorder: boolean;
    icon: React.ReactNode;
    showDefaultBadge: boolean;
}

const SortableViewChip: React.FC<SortableViewChipProps> = ({
    view,
    selectedViewId,
    onViewSelect,
    onEditView,
    isAdmin,
    isLoading,
    canReorder,
    icon,
    showDefaultBadge,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: view.id,
        disabled: !canReorder,
    });

    const style = canReorder
        ? {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.5 : 1,
          }
        : undefined;

    const chip = (
        <div
            ref={canReorder ? setNodeRef : undefined}
            style={style}
            className="flex items-center gap-1"
        >
            {canReorder && (
                <span
                    className="cursor-grab active:cursor-grabbing touch-none p-0.5 rounded text-muted-foreground hover:text-foreground"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="h-3.5 w-3.5" />
                </span>
            )}
            <Button
                variant={selectedViewId === view.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => onViewSelect(selectedViewId === view.id ? null : view.id)}
                className="text-xs flex items-center gap-1"
                disabled={isLoading}
            >
                {icon}
                {view.name}
                {showDefaultBadge && view.is_default && (
                    <Badge variant="secondary" className="text-xs ml-1">
                        Default
                    </Badge>
                )}
            </Button>
            {(isAdmin || !view.is_global) && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditView(view)}
                    className="h-7 w-7 p-0"
                    disabled={isLoading}
                >
                    <Edit className="h-3 w-3" />
                </Button>
            )}
        </div>
    );

    return chip;
};
